import {
  CCSBodyPickerQuestionIds,
  CCSPrimaryQuestionIds,
  CCSSecondaryQuestionIds,
  ReevalSurveyQuestionIds,
} from '@chiroup/core/constants/CCSQuestionIds';
import { Genders } from '@chiroup/core/enums/Genders.enum';
import { createName } from '@chiroup/core/functions/createName';
import { replaceComplaintMergeField } from '@chiroup/core/functions/replaceComplaintMergeField';
import { RequestStep } from '@chiroup/core/types/Request.type';
import { SurveyQuestion, SurveyQuestionAnswer } from '@chiroup/core/types/Survey.type';
import * as Sentry from '@sentry/react';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '../Alert';
import { SoftSurveyQuestion } from '../survey/soft';
import Body from './Body';
import CCSInstructions from './CCSInstructions';
import { createReevalQuestions } from './ccsQuestions';
import {
  bodyPickerSubmit,
  ccsBodyPickerQuestion,
  ccsBodyPickerQuestionSpanish,
  expandSpecialQuestion,
  getNextQuestionIndex,
  isSpecialQuestion,
  submitStuff,
} from './helpers';
import { recoverStaleSurveySession } from './recoverStaleSurveySession';

const submitVerbiage = {
  more: {
    en: 'Hang tight, we have a few more things we need to ask...',
    es: 'Espera un momento, tenemos algunas cosas más que debemos preguntar...',
  },
  done: { en: 'Submitting survey', es: 'La encuesta esta siendo enviada' },
};

/**
 * Build per-complaint questions from a custom re-eval survey template.
 * Extracted from ReevalSurvey's render body so the survey preview modal
 * (and parity tests) can call it without mounting the component.
 *
 * Mirrors addCustomCCSQuestions logic: replace merge fields, set problemArea,
 * and expand special question types (provocative/palliative).
 */
/**
 * Strip template-only metadata and undefined `typeOptions` so the expanded
 * custom-path output matches the structural shape the standard generator
 * (createReevalQuestions) produces. Without this strip, custom-path questions
 * leak `templateAlias` into the patient-facing payload.
 */
const stripReevalTemplateMetadata = (q: SurveyQuestion): SurveyQuestion => {
  const { templateAlias, showOnVisit, typeOptions, ...rest } = q as any;
  if (typeOptions !== undefined) {
    return { ...rest, typeOptions };
  }
  return rest;
};

/**
 * Standard generator (ccsQuestions.ts:117-121) appends a Dizziness option to
 * the "describe symptoms" question when the complaint region includes 'head'.
 * Replicate that here so custom-imported templates render the same option set.
 */
const augmentDescribeOptionsForRegion = (
  q: SurveyQuestion,
  regionName: string,
  lang: string
): SurveyQuestion => {
  if (!regionName?.toLowerCase().includes('head')) return q;
  const dizziness = {
    id: 'Dizziness',
    display: lang === 'es' ? 'Convulsiones Mareo' : 'Dizziness',
  };
  const opts = (q.options || []).filter(o => o?.id !== 'Dizziness');
  return { ...q, options: [...opts, dizziness] };
};

export const buildCustomComplaintQuestions = (
  customQuestions: SurveyQuestion[] | undefined,
  parts: any,
  problemArea: number,
  complaintId: string,
  lang: string
): SurveyQuestion[] => {
  if (!customQuestions?.length) return [];
  const regionName = createName(parts);
  const templateQuestions = customQuestions.filter(q => q.type !== 'bodypicker');

  // Scope conditional references (if/ifSome) so they point at the
  // per-complaint copies of the questions they depend on.
  const scopeConditions = (conditions: SurveyQuestion['if']): SurveyQuestion['if'] => {
    if (!conditions?.length) return conditions;
    return conditions.map(c => (c?.id ? { ...c, id: `${c.id}-${complaintId}` } : c));
  };

  return templateQuestions.map(q => {
    const specialType = isSpecialQuestion(q);

    if (specialType) {
      const expanded = expandSpecialQuestion(q, parts, regionName, lang, problemArea, specialType);
      return stripReevalTemplateMetadata({
        ...expanded,
        id: `${expanded.id}-${complaintId}`,
        complaintId,
        if: scopeConditions(expanded.if),
        ifSome: scopeConditions(expanded.ifSome),
      });
    }

    // Route on stable question ID (not user-editable templateAlias). The
    // "describe" question in the re-eval import template uses the primary CCS
    // mainQuestionId; the standard generator (ccsQuestions.ts:117-121) appends
    // a Dizziness option for head complaints, and we mirror that here.
    const isDescribeQuestion =
      q.id === CCSPrimaryQuestionIds.mainQuestionId ||
      q.id === CCSSecondaryQuestionIds.mainQuestionId;
    const adjusted = isDescribeQuestion ? augmentDescribeOptionsForRegion(q, regionName, lang) : q;

    return stripReevalTemplateMetadata({
      ...adjusted,
      id: `${adjusted.id}-${complaintId}`,
      question: adjusted.question
        ? replaceComplaintMergeField(adjusted.question, regionName)
        : adjusted.question,
      problemArea,
      complaintId,
      if: scopeConditions(adjusted.if),
      ifSome: scopeConditions(adjusted.ifSome),
    });
  });
};

type Props = {
  complaints: any;
  primaryColor: string;
  language: 'en' | 'es' | undefined;
  complete: () => void;
  ccsFollowups?: string[];
  kiosk: boolean;
  prMap: { [key: string]: string };
  startQuestion: SurveyQuestion;
  markSurveyComplete: () => void;
  updateRequest?: (
    data: {
      prMap: { [key: string]: string };
      steps: RequestStep[];
      language: 'en' | 'es';
      gender: Genders;
      age: number;
      id: string;
    },
    callback?: () => void
  ) => void;
  validationCode: string | number;
  surveyIds?: string[];
  weeks?: number;
  age: number;
  gender: Genders;
  submitSurvey: (
    questions: SurveyQuestion[],
    kiosk: boolean,
    spanishQuestions?: SurveyQuestion[],
    ccsFollowupSurveyIds?: string[],
    validationCode?: string | number
  ) => Promise<any>;
  myScreen?: boolean;
  setLoadingInterimOrReevalResults?: Dispatch<SetStateAction<boolean>>;
  customQuestions?: SurveyQuestion[];
  customCCSSurveyId?: string;
  allowAdditionalComplaints?: boolean;
};

const buildNewProblemsQuestion = (lang: 'en' | 'es'): SurveyQuestion =>
  ({
    id: ReevalSurveyQuestionIds.reevalNewProblemsQuestionId,
    type: 'radio',
    question:
      lang === 'es'
        ? 'Desde la última visita, ¿tiene algún problema importante y nuevo que le gustaría informar?'
        : 'Since the last visit, do you have any significant and new problems you would like to report?',
    options: [
      { id: 'Yes', display: lang === 'es' ? 'Sí' : 'Yes' },
      { id: 'No', display: 'No' },
    ],
  }) as SurveyQuestion;

const hasNewProblemsQuestion = (list: SurveyQuestion[] | undefined): boolean =>
  !!list?.some(
    q =>
      q?.id === ReevalSurveyQuestionIds.reevalNewProblemsQuestionId ||
      q?.id === 'reeval-newProblems'
  );

export const ReevalSurvey = ({
  complaints,
  primaryColor,
  language,
  complete,
  ccsFollowups,
  markSurveyComplete,
  updateRequest,
  validationCode,
  surveyIds,
  submitSurvey,
  startQuestion,
  weeks,
  kiosk,
  age,
  gender,
  prMap,
  myScreen,
  setLoadingInterimOrReevalResults,
  customQuestions,
  customCCSSurveyId,
  allowAdditionalComplaints,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [spanishQuestions, setSpanishQuestions] = useState<SurveyQuestion[]>([startQuestion]);
  const [currentQuestion, setCurrentQuestion] = useState<any>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [submitText, setSubmitText] = useState<string>(
    submitVerbiage.done[language === 'es' ? 'es' : 'en']
  );
  const [disabled, setDisabled] = useState(false);
  const [show, setShow] = useState<boolean>(false);
  const [aaWI, setAaWI] = useState<string>();
  const [instructions, setInstructions] = useState<boolean>(true);
  const [surveyCreated, setSurveyCreated] = useState<boolean>(false);

  const buildCustomComplaintQuestionsForComponent = useCallback(
    (parts: any, problemArea: number, complaintId: string, lang: string): SurveyQuestion[] =>
      buildCustomComplaintQuestions(customQuestions, parts, problemArea, complaintId, lang),
    [customQuestions]
  );
  const modalHeight = useRef(0);
  const progress = useMemo(() => {
    const questionsCopy = [...questions];
    const total = questionsCopy?.length;
    const reverseIndexOfLastAnswer = questionsCopy.reverse().findIndex(q => !!q.answer);
    const reverseIndexOfLastAnswerOrZero =
      reverseIndexOfLastAnswer > -1 ? reverseIndexOfLastAnswer : questionsCopy.length;
    const completed = questionsCopy.length - reverseIndexOfLastAnswerOrZero;

    return {
      completed: completed || 0,
      total: total || 1,
      // Reevaluations branch per-complaint, so the total shifts mid-survey —
      // show the bar only, no jumping count (Monday 12560530217).
      hideCount: true,
    };
  }, [questions]);

  useEffect(() => {
    const height = document.getElementById('survey-modal');

    if (height) {
      modalHeight.current = height.clientHeight;
    }
  }, [progress]);

  const disableSurvey = async () => {
    return new Promise(resolve => {
      setDisabled(true);
      setShow(false);
      // Wait 333ms for the question to disappear
      setTimeout(() => {
        resolve(true);
      }, 333);
    });
  };

  const enableSurvey = async () => {
    return new Promise(resolve => {
      // Wait 333ms for the question to have been changed
      setTimeout(() => {
        setDisabled(false);
        setShow(true);
        setIsSubmitting(false);
        resolve(true);
      }, 333);
    });
  };

  useEffect(() => {
    enableSurvey();
  }, []);

  const onSelect = async (val: SurveyQuestionAnswer) => {
    await disableSurvey();

    if (
      val.value === (language === 'es' ? 'Accidente automovilístico' : 'Auto accident') ||
      val.value ===
        (language === 'es' ? 'Lesión relacionada con el empleo' : 'Employment-related injury')
    ) {
      setAaWI(val.value);
    }

    const currentQuestionIndexEn = questions.findIndex(
      (q: { id: string }) => q.id === currentQuestion.id
    );

    const newQuestions = [...questions];
    const newQuestionsSpanish = spanishQuestions ? [...spanishQuestions] : [];

    // Language-agnostic: prefer option id over display value
    const selectedOptionId =
      (val as any)?.id ||
      currentQuestion?.options?.find((o: any) => o.display === val?.value)?.id ||
      (typeof val?.value === 'string' ? (val as any).value : undefined);
    const yesOptionId = currentQuestion?.options?.find((o: any) => o.id === 'Yes')?.id || 'Yes';

    const isNewProblemsQuestion =
      currentQuestion.id === ReevalSurveyQuestionIds.reevalNewProblemsQuestionId ||
      currentQuestion.id === 'reeval-newProblems';

    if (isNewProblemsQuestion && selectedOptionId === yesOptionId) {
      newQuestions.push(ccsBodyPickerQuestion);

      if (language === 'es') {
        newQuestionsSpanish.push(ccsBodyPickerQuestionSpanish);
      }
    }

    // If there are existing complaints, add the re-eval questions for each complaint
    // but make sure to deduplicate to avoid showing the same questions multiple times
    if (complaints && complaints.length > 0) {
      // Create a set of existing question IDs to avoid duplicates
      const existingQuestionIds = new Set(newQuestions.map(q => q.id));

      complaints.forEach((complaint: any, index: number) => {
        const parts = complaint?.bodypicker?.parts;
        const problemArea = index + 1;
        const complaintId = complaint?.id;
        const lastQuestionOrder = newQuestions.length - 1;

        if (parts) {
          // Use custom questions when a custom survey is configured
          const englishQuestions = customQuestions?.length
            ? buildCustomComplaintQuestionsForComponent(parts, problemArea, complaintId, 'en')
            : createReevalQuestions(
                parts,
                problemArea,
                complaintId,
                lastQuestionOrder,
                currentQuestion.id === CCSBodyPickerQuestionIds.primary
                  ? CCSPrimaryQuestionIds
                  : CCSSecondaryQuestionIds,
                'en',
                aaWI,
                weeks
              );

          // Only add questions that don't already exist
          const uniqueEnglishQuestions = englishQuestions.filter(
            q => !existingQuestionIds.has(q.id)
          );

          if (uniqueEnglishQuestions.length > 0) {
            newQuestions.push(...uniqueEnglishQuestions);
            // Update our set of existing IDs
            uniqueEnglishQuestions.forEach(q => existingQuestionIds.add(q.id));
          }

          // Add Spanish questions if needed
          if (language === 'es') {
            const existingSpanishQuestionIds = new Set(newQuestionsSpanish.map(q => q.id));

            const spanishReevalQuestions = customQuestions?.length
              ? buildCustomComplaintQuestionsForComponent(parts, problemArea, complaintId, 'es')
              : createReevalQuestions(
                  parts,
                  problemArea,
                  complaintId,
                  lastQuestionOrder,
                  currentQuestion.id === CCSBodyPickerQuestionIds.primary
                    ? CCSPrimaryQuestionIds
                    : CCSSecondaryQuestionIds,
                  'es',
                  aaWI,
                  weeks
                );

            // Only add Spanish questions that don't already exist
            const uniqueSpanishQuestions = spanishReevalQuestions.filter(
              q => !existingSpanishQuestionIds.has(q.id)
            );

            if (uniqueSpanishQuestions.length > 0) {
              newQuestionsSpanish.push(...uniqueSpanishQuestions);
            }
          }
        }
      });
    }

    // Ensure Spanish start/new-problem question stays at the very end of the flow
    if (language === 'es' && Array.isArray(newQuestionsSpanish) && newQuestionsSpanish.length) {
      const npId = ReevalSurveyQuestionIds.reevalNewProblemsQuestionId;
      const idx = newQuestionsSpanish.findIndex(
        (q: any) => q?.id === npId || q?.id === 'reeval-newProblems'
      );

      if (idx >= 0 && idx !== newQuestionsSpanish.length - 1) {
        const [np] = newQuestionsSpanish.splice(idx, 1);
        newQuestionsSpanish.push(np);
      }
    }

    if (currentQuestionIndexEn >= 0 && newQuestions[currentQuestionIndexEn]) {
      newQuestions[currentQuestionIndexEn].answer = val;
    }

    if (language === 'es') {
      const currentQuestionIndexEs = newQuestionsSpanish.findIndex(
        (q: { id: string }) => q.id === currentQuestion.id
      );

      if (currentQuestionIndexEs >= 0 && newQuestionsSpanish?.[currentQuestionIndexEs]) {
        newQuestionsSpanish[currentQuestionIndexEs].answer = val;
      }
    }

    const baseAfter = language === 'es' ? newQuestionsSpanish : newQuestions;
    const nextQuestionIndex = getNextQuestionIndex({ questions: baseAfter });
    const nextQuestion = (baseAfter || [])?.[nextQuestionIndex];

    if (!nextQuestion) {
      console.warn('[Reeval] No nextQuestion computed. Submitting survey.');
      setDisabled(false);
      setLoadingInterimOrReevalResults?.(true);
      await submitStuff({
        questions: questions,
        kiosk: kiosk,
        spanishQuestions,
        setIsSubmitting,
        isSubmitting,
        submitSurvey,
        complete,
        complaints,
        markSurveyComplete,
        setSubmitText,
        submitVerbiage,
        validationCode,
        language,
        surveyIds,
        ccsFollowups,
        customCCSSurveyId,
        updateRequest,
      }).catch(err => {
        console.error(err);

        if (
          recoverStaleSurveySession({
            err,
            kiosk,
            language,
            currentQuestionId: currentQuestion?.id,
          })
        ) {
          return;
        }

        const errorMessage =
          err?.response?.data?.message || err?.message || 'Survey submission failed';
        Sentry.captureException(new Error(errorMessage));
        enableSurvey();
        setCurrentQuestion((prev: any) => {
          return { ...prev, answer: null };
        });
        setHasError(true);
      });
      setLoadingInterimOrReevalResults?.(false);

      return;
    }

    setQuestions(newQuestions);

    if (setSpanishQuestions && language === 'es') {
      setSpanishQuestions(newQuestionsSpanish);
    }

    // Resolve `notLessThan` / `notMoreThan` references onto a render-
    // time clone — the array entry keeps its template string so a
    // forward pass after a back-edit re-resolves against the latest
    // referenced answer.
    let renderedNextQuestion = nextQuestion;
    const nextOpts = nextQuestion?.typeOptions;
    const hasNotLessThanRef = typeof nextOpts?.notLessThan === 'string';
    const hasNotMoreThanRef = typeof nextOpts?.notMoreThan === 'string';

    if (hasNotLessThanRef || hasNotMoreThanRef) {
      const resolvedTypeOptions = { ...nextOpts };
      if (typeof resolvedTypeOptions.notLessThan === 'string') {
        resolvedTypeOptions.notLessThan = getValueOfQuestion(resolvedTypeOptions.notLessThan);
      }
      if (typeof resolvedTypeOptions.notMoreThan === 'string') {
        resolvedTypeOptions.notMoreThan = getValueOfQuestion(resolvedTypeOptions.notMoreThan);
      }
      renderedNextQuestion = { ...nextQuestion, typeOptions: resolvedTypeOptions };
    }

    setCurrentQuestion(renderedNextQuestion);
    await enableSurvey();

    return;
  };

  const getValueOfQuestion = (questionId: string) => {
    const question = questions.find((question: { id: string }) => question.id === questionId);

    if (question) {
      return question.answer?.value;
    }
  };

  useEffect(() => {
    // BUGFIX: Don't reset questions/currentQuestion if survey has already been created
    // This was causing the survey to jump to the last question when dependencies changed
    if (surveyCreated) {
      return;
    }

    // When custom questions are provided and complaints are pre-populated,
    // the expansion useEffect below handles question generation. Don't seed
    // the array with the raw startQuestion — it contains unresolved
    // {complaint} placeholders that would appear at the end of the survey.
    const complaintsPrePopulated = customQuestions?.length && complaints?.[0]?.bodypicker?.parts;

    if (language === 'es' && complaints?.length) {
      setQuestions(complaintsPrePopulated ? [] : [startQuestion]);

      return;
    }

    if (complaintsPrePopulated) {
      setQuestions([]);
    } else if (startQuestion) {
      setCurrentQuestion(startQuestion);
      setQuestions([startQuestion]);
    } else {
      setQuestions([]);
    }
  }, [startQuestion, language, complaints, surveyCreated, customQuestions]);

  useEffect(() => {
    if (!surveyCreated) {
      if (complaints?.[0]?.bodypicker?.parts) {
        complaints.forEach((complaint: any, index: number) => {
          const parts = complaint?.bodypicker?.parts;
          const problemArea = index + 1;
          const complaintId = complaint?.id;
          const currentQuestionId = currentQuestion?.id;
          const lastQuestionOrder = questions?.length - 1;

          const englishQuestions = customQuestions?.length
            ? buildCustomComplaintQuestionsForComponent(parts, problemArea, complaintId, 'en')
            : createReevalQuestions(
                parts,
                problemArea,
                complaintId,
                lastQuestionOrder,
                currentQuestionId === CCSBodyPickerQuestionIds.primary
                  ? CCSPrimaryQuestionIds
                  : CCSSecondaryQuestionIds,
                'en',
                aaWI,
                weeks
              );

          const newEnglishQuestions = questions.concat(englishQuestions);

          const builtSpanishQuestions = customQuestions?.length
            ? buildCustomComplaintQuestionsForComponent(parts, problemArea, complaintId, 'es')
            : createReevalQuestions(
                parts,
                problemArea,
                complaintId,
                lastQuestionOrder,
                currentQuestionId === CCSBodyPickerQuestionIds.primary
                  ? CCSPrimaryQuestionIds
                  : CCSSecondaryQuestionIds,
                'es',
                aaWI,
                weeks
              );

          // Frontend-only Spanish alignment: ensure complaint questions come before the Spanish start question
          const newSpanishQuestions = spanishQuestions
            ? builtSpanishQuestions.concat(spanishQuestions)
            : builtSpanishQuestions;

          const base = language === 'es' ? newSpanishQuestions : newEnglishQuestions;
          const nextQuestionIndex = getNextQuestionIndex({
            questions: base,
          });
          const nextQuestion = (base || [])?.[nextQuestionIndex];
          setCurrentQuestion(nextQuestion);
          setQuestions(prev => {
            return [...newEnglishQuestions, ...prev];
          });

          if (setSpanishQuestions && language === 'es') {
            setSpanishQuestions(newSpanishQuestions || []);
          }
        });

        if (customQuestions?.length && allowAdditionalComplaints) {
          setQuestions(prev =>
            hasNewProblemsQuestion(prev) ? prev : [...prev, buildNewProblemsQuestion('en')]
          );
          if (language === 'es') {
            setSpanishQuestions(prev => {
              const list = prev || [];
              return hasNewProblemsQuestion(list)
                ? list
                : [...list, buildNewProblemsQuestion('es')];
            });
          }
        }

        setSurveyCreated(true);
        setTimeout(() => setShow(true), 333);
      }
    }
  }, [
    complaints,
    aaWI,
    currentQuestion?.id,
    language,
    questions,
    spanishQuestions,
    weeks,
    surveyCreated,
    customQuestions,
    allowAdditionalComplaints,
  ]);

  const goBack = async () => {
    await disableSurvey();
    const currentQuestionIndex = questions.findIndex(
      (q: { id: string }) => q.id === currentQuestion.id
    );
    const mostRecentQuestionThatWasAnswered = questions
      .slice(0, currentQuestionIndex)
      .reverse()
      .find(q => q.answer);
    const mostRecentQuestionThatWasAnsweredIndex = questions.findIndex(
      q => q.id === mostRecentQuestionThatWasAnswered?.id
    );

    if (!mostRecentQuestionThatWasAnswered) {
      return;
    }

    if (mostRecentQuestionThatWasAnswered?.type === 'bodypicker') {
      const thisProblemArea = mostRecentQuestionThatWasAnswered.problemArea;
      setQuestions(prev => {
        const questionsBeforeIndex = prev.slice(0, mostRecentQuestionThatWasAnsweredIndex + 1);

        return questionsBeforeIndex.filter(
          q => q.problemArea !== thisProblemArea || q.type === 'bodypicker'
        );
      });
    } else {
      setQuestions(prev => {
        return prev.map((q, i) => {
          if (i > mostRecentQuestionThatWasAnsweredIndex) {
            return { ...q, answer: undefined };
          }

          return q;
        });
      });
    }

    setCurrentQuestion({ ...mostRecentQuestionThatWasAnswered, answer: null });
    await enableSurvey();
  };

  return instructions && currentQuestion?.type === 'bodypicker' && !myScreen ? (
    <CCSInstructions
      setInstructions={setInstructions}
      language={language}
      primaryColor={primaryColor}
    />
  ) : (
    <div className="flex flex-col gap-4">
      {!!hasError && show && (
        <Alert title="Error" error="There was an error submitting your survey. Please try again." />
      )}
      {currentQuestion &&
        (currentQuestion?.type === 'bodypicker' ? (
          <Body
            show={show}
            bodyPickerSubmit={bodyPickerSubmit}
            language={language}
            id={currentQuestion.id}
            primaryColor={primaryColor}
            spanishQuestions={spanishQuestions}
            questions={questions}
            currentQuestion={currentQuestion}
            setSpanishQuestions={setSpanishQuestions}
            setQuestions={setQuestions}
            setCurrentQuestion={setCurrentQuestion}
            aaWI={aaWI}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
            complete={complete}
            markSurveyComplete={markSurveyComplete}
            submitSurvey={submitSurvey}
            submitVerbiage={submitVerbiage}
            setSubmitText={setSubmitText}
            validationCode={validationCode}
            surveyIds={surveyIds}
            ccsFollowups={ccsFollowups}
            updateRequest={updateRequest}
            startingProblemArea={complaints.length}
            kiosk={kiosk}
            additionalComplaints={false}
          />
        ) : (
          // Non-bodypicker questions use the shared soft card UI so
          // reevaluation surveys match the rest of the new portal
          // flow. The `parts` / `source` props were CCS-picker hints
          // for the legacy renderer; the soft renderer doesn't need
          // them and they're already represented in the prior
          // bodypicker question.
          <SoftSurveyQuestion
            // Force a remount per question so per-question internal
            // state (e.g. slider `value`) doesn't leak from a max=100
            // slider into a subsequent max=10 slider. See the
            // matching comment in ChiefComplaintSurvey.tsx.
            key={currentQuestion.id}
            question={currentQuestion}
            language={language === 'es' ? 'es' : 'en'}
            primaryColor={primaryColor}
            disabled={disabled || !!currentQuestion.answer}
            hidden={!show}
            showPrevious
            onBack={goBack}
            onSelect={onSelect}
            progress={progress}
          />
        ))}
    </div>
  );
};
